import { useExtractDocs } from "context/ExtractDocsContext";
import { QUERY_PROGRESS_PENDING, TRACK_EVENTS } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { FileTableActions, Filev2, QueryProgress } from "core/types";
import GeneralTable from "ds_legacy/components/Tables/GeneralTable";
import {
  COLUMN_TYPE_DEFAULT_DATE,
  COLUMN_TYPE_FILES_CATEGORY,
  COLUMN_TYPE_FILES_FILE_NAME,
} from "ds_legacy/components/Tables/GeneralTable/CustomCells";
import { EmptyV2 } from "ds_legacy/components/Tables/GeneralTable/EmptyComponent";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  getAllowList,
  getFileListActionOptions,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions/Files";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import {
  EncryptedFileMessage,
  FilesTableTitle,
} from "dsl/organisms/FileTables";
import { useMemo } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

/** Column width percentages for SentFiles — must sum to 100%. Min widths avoid truncation (e.g. category needs room for label + icon). */
const SENT_FILES_WIDTHS = {
  default: { category: "10%", fileName: "28%", shareDate: "62%" },
  messenger: { category: "18%", fileName: "40%", shareDate: "42%" },
} as const;

export type SentFilesProps = {
  canSendFiles?: boolean;
  files?: Filev2[] | null;
  isMessenger?: boolean;
  queryProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
};

export function SentFiles({
  canSendFiles = true,
  files,
  isMessenger,
  queryProgress,
  setAction,
}: SentFilesProps) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { extractOcrStatus, utils } = useExtractDocs();

  const extractionStatusKey = useMemo(
    () =>
      extractOcrStatus.result?.documents
        .map((doc) => `${doc.recare_file_id || doc.kis_file_id}:${doc.status}`)
        .sort()
        .join(",") ?? "",
    [extractOcrStatus.result],
  );

  const isExtractActive = isDocsAndExtractActive || isDischargeLetterActive;

  return (
    <VerticalLayout>
      <FilesTableTitle
        description={isMessenger ? <EncryptedFileMessage /> : ""}
        isMessenger={isMessenger}
        numberOfFiles={files?.length ?? 0}
        title={translations.fileSection.fileListComponent.titleFilesSent}
      />

      <GeneralTable<Filev2, FileTableActions>
        actions={{
          allowList: () => getAllowList({ fileTable: "sent" }),
          getOptions: ({ data: file, translations }) =>
            getFileListActionOptions({
              canAccessPatientData: true,
              file,
              fileTable: "sent",
              isExtractActive,
              isFileExtracted: utils.isFileExtracted(file),
              setAction,
              trackEvent,
              translations,
            }),
          getTestId: (data) => `action-sent-file-${data.id}`,
          onActionMenuClick: () => {
            trackEvent({
              file_implementation: "HIS",
              name: TRACK_EVENTS.FILE_MENU_CLICKED,
              section: "sent",
            });
          },
        }}
        columns={[
          {
            getProps: "category",
            title: translations.fileSection.fileListComponent.category,
            type: COLUMN_TYPE_FILES_CATEGORY,
            width: isMessenger
              ? SENT_FILES_WIDTHS.messenger.category
              : SENT_FILES_WIDTHS.default.category,
          },
          {
            getProps: (file) =>
              getFileNameAndIsNewVersionAvailable({
                canAccessPatientData: true,
                file,
              }),
            title: translations.fileSection.fileListComponent.fileName,
            type: COLUMN_TYPE_FILES_FILE_NAME,
            width: isMessenger
              ? SENT_FILES_WIDTHS.messenger.fileName
              : SENT_FILES_WIDTHS.default.fileName,
          },
          {
            getProps: ({ created_at }) => ({
              value: created_at,
            }),
            title: translations.fileSection.fileListComponent.shareDate,
            type: COLUMN_TYPE_DEFAULT_DATE,
            width: isMessenger
              ? SENT_FILES_WIDTHS.messenger.shareDate
              : SENT_FILES_WIDTHS.default.shareDate,
          },
        ]}
        data={files || []}
        dependencies={[extractionStatusKey]}
        emptyComponent={
          <EmptyV2
            isMessenger={isMessenger}
            title={
              canSendFiles
                ? translations.fileSection.fileListComponent
                    .placeholderNothingSent
                : translations.messenger.receiver.fileRestrictionHint
            }
          />
        }
        id="sent-files"
        isLoading={queryProgress === QUERY_PROGRESS_PENDING}
      />
    </VerticalLayout>
  );
}

export default SentFiles;
